import { useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TranslationFunctions } from '@/i18n/i18n-types';
import {
  getDelayParts,
  WELCOME_DELAY_CHIPS,
  WELCOME_DELAY_MINUTES,
  type WelcomeDelay,
} from '@/lib/app-editor/welcome-notification-form';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

export interface WelcomeDelayPickerProps {
  value: WelcomeDelay;
  onChange: (delay: WelcomeDelay) => void;
}

/** "Imediatamente", "45 minutos", "1 hora", "2 dias"... */
export function formatWelcomeDelay(
  delay: WelcomeDelay,
  LL: TranslationFunctions
): string {
  const parts = getDelayParts(delay);
  if (parts.kind === 'immediately')
    return LL.appCustomizationPage.welcomeNotifications.delayOptions.immediately();
  const { amount } = parts;
  const unit = {
    minutes:
      LL.appCustomizationPage.welcomeNotifications.delayOptions.minutes(),
    hours:
      amount === 1
        ? LL.appCustomizationPage.welcomeNotifications.delayOptions.hour()
        : LL.appCustomizationPage.welcomeNotifications.delayOptions.hours(),
    days:
      amount === 1
        ? LL.appCustomizationPage.welcomeNotifications.delayOptions.day()
        : LL.appCustomizationPage.welcomeNotifications.delayOptions.days(),
  }[parts.kind];
  return `${amount} ${unit}`;
}

function formatChip(delay: WelcomeDelay, LL: TranslationFunctions): string {
  const parts = getDelayParts(delay);
  if (parts.kind === 'immediately')
    return LL.welcomeEditor.delay.chipImmediately();
  const { amount } = parts;
  return {
    minutes: LL.welcomeEditor.delay.chipMinutes({ amount }),
    hours: LL.welcomeEditor.delay.chipHours({ amount }),
    days: LL.welcomeEditor.delay.chipDays({ amount }),
  }[parts.kind];
}

// Chips are a phone idiom: round only below 768px (AD-016).
const CHIP_CLASS = cn(
  'rounded-md border border-input px-3 py-1.5 text-sm font-medium max-md:rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground',
  mobileLayer.touch
);

export function WelcomeDelayPicker({
  value,
  onChange,
}: WelcomeDelayPickerProps) {
  const { LL } = useI18nContext();
  const isChip = WELCOME_DELAY_CHIPS.includes(value);
  const [expanded, setExpanded] = useState(!isChip);

  return (
    <div className="space-y-3">
      <div
        role="radiogroup"
        aria-label={LL.welcomeEditor.delay.label()}
        className="flex flex-wrap gap-2"
      >
        {WELCOME_DELAY_CHIPS.map((chipValue) => (
          <button
            key={chipValue}
            type="button"
            role="radio"
            aria-checked={chipValue === value}
            className={CHIP_CLASS}
            onClick={() => onChange(chipValue)}
          >
            {formatChip(chipValue, LL)}
          </button>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={!isChip}
          className={cn(CHIP_CLASS, 'flex items-center gap-1.5')}
          onClick={() => setExpanded((open) => !open)}
        >
          <span>{LL.welcomeEditor.delay.other()}</span>
          {!isChip && <span>{formatWelcomeDelay(value, LL)}</span>}
        </button>
      </div>
      {expanded && (
        <div
          role="radiogroup"
          aria-label={LL.welcomeEditor.delay.all()}
          className="grid grid-cols-2 gap-2 sm:grid-cols-3"
        >
          {WELCOME_DELAY_MINUTES.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={option === value}
              className={CHIP_CLASS}
              onClick={() => onChange(option)}
            >
              {formatWelcomeDelay(option, LL)}
            </button>
          ))}
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        {value === 0
          ? LL.welcomeEditor.delay.sentenceImmediately()
          : LL.welcomeEditor.delay.sentence({
              delay: formatWelcomeDelay(value, LL),
            })}
      </p>
    </div>
  );
}
